import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useTranslation } from '@client-app/i18n';
import { featureService } from '@common/api/featureService';
import { Check, X } from 'lucide-react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ContactSalesModal.module.scss';

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  jobTitle: string;
  companySize: string;
  brandCount: string;
  primaryUseCase: string;
  needs: string;
}

const ContactSalesModal: React.FC<ContactSalesModalProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { userBilling } = useAuth();
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
        jobTitle: '',
        companySize: '',
        brandCount: '',
        primaryUseCase: '',
        needs: ''
    });

    const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    if (!isOpen) return null;

    const validateForm = () => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = t('website.pricingContactSales.errors.firstNameRequired');
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = t('website.pricingContactSales.errors.lastNameRequired');
        }

        if (!formData.email.trim()) {
            newErrors.email = t('website.pricingContactSales.errors.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = t('website.pricingContactSales.errors.emailInvalid');
        }

        if (!formData.companyName.trim()) {
            newErrors.companyName = t('website.pricingContactSales.errors.companyNameRequired');
        }

        if (!formData.companySize) {
            newErrors.companySize = t('website.pricingContactSales.errors.companySizeRequired');
        }

        if (!formData.brandCount) {
            newErrors.brandCount = t('website.pricingContactSales.errors.brandCountRequired');
        }

        if (!formData.primaryUseCase) {
            newErrors.primaryUseCase = t('website.pricingContactSales.errors.primaryUseCaseRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
    
        if (validateForm()) {
            try {
                await featureService.sendGenericMessage({
                    title: "Talk to Sales",
                    content: {
                        "First Name": formData.firstName,
                        "Last Name": formData.lastName,
                        "Work Email": formData.email,
                        "Company Name": formData.companyName,
                        "Job Title": formData.jobTitle || 'N/A',
                        "Company Size": formData.companySize,
                        "Number of Brands/Stores": formData.brandCount,
                        "Current Plan": userBilling?.planText?.planDisplayText || 'N/A',
                        "Primary Use Case": formData.primaryUseCase,
                        "Needs": formData.needs || 'N/A'
                    }
                });
                setIsSubmitted(true);
                setErrors({});
            } catch (error) {
                console.error("Failed to send contact sales request", error);
                showToast(t('website.pricingContactSales.submitFailed'), "error");
            }
        }
    };

    const handleClose = () => {
        onClose();
        // Reset state after a short delay
        setTimeout(() => {
            setIsSubmitted(false);
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                companyName: '',
                jobTitle: '',
                companySize: '',
                brandCount: '',
                primaryUseCase: '',
                needs: ''
            });
        }, 200);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user types
        if (errors[name as keyof FormData]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const modalContent = (
        <div className={styles.overlay} onClick={handleClose}>
            <div className={styles.container}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    {isSubmitted ? (
                        <div className={styles.content}>
                            {/* Close Button absolute inside container for success state */}
                            <button
                                onClick={handleClose}
                                className={styles.closeButton}
                                aria-label={t('common.closeModal')}
                            >
                                <X size={24} />
                            </button>
                            <div className={styles.successState}>
                                <div className={styles.successIcon}>
                                    <Check size={40} className={styles.checkIcon} strokeWidth={3} />
                                </div>
                                <h2 className={styles.successTitle}>{t('website.pricingContactSales.successTitle')}</h2>
                                <p className={styles.successSubtitle}>{t('website.pricingContactSales.successSubtitle')}</p>
                                <p className={styles.successMessage}>
                                    {t('website.pricingContactSales.successMessage')}
                                </p>
                                <button
                                    onClick={handleClose}
                                    className={styles.closeSuccessButton}
                                >
                                    {t('website.pricingContactSales.close')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Sticky/Fixed Modal Header */}
                            <div className={styles.header}>
                                <div className={styles.headerText}>
                                    <h2 className={styles.title}>{t('website.pricingContactSales.title')}</h2>
                                    <p className={styles.subtitle}>{t('website.pricingContactSales.description')}</p>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className={styles.closeButtonInsideHeader}
                                    aria-label={t('common.closeModal')}
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Scrollable Form Content */}
                            <div className={styles.content}>
                                <form onSubmit={handleSubmit} className={styles.form} noValidate>
                                    <div className={styles.grid}>
                                        {/* First Name */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                {t('website.pricingContactSales.firstName')}
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="firstName"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                placeholder={t('website.pricingContactSales.firstNamePlaceholder')}
                                                className={`${styles.input} ${errors.firstName ? styles.errorInput : ''}`}
                                            />
                                            {errors.firstName ? <p className={styles.errorMessage}>{errors.firstName}</p> : null}
                                        </div>

                                        {/* Last Name */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                {t('website.pricingContactSales.lastName')}
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="lastName"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                placeholder={t('website.pricingContactSales.lastNamePlaceholder')}
                                                className={`${styles.input} ${errors.lastName ? styles.errorInput : ''}`}
                                            />
                                            {errors.lastName ? <p className={styles.errorMessage}>{errors.lastName}</p> : null}
                                        </div>

                                        {/* Work Email */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                {t('website.pricingContactSales.workEmail')}
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="you@company.com"
                                                className={`${styles.input} ${errors.email ? styles.errorInput : ''}`}
                                            />
                                            {errors.email ? <p className={styles.errorMessage}>{errors.email}</p> : null}
                                        </div>

                                        {/* Company Name */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                {t('website.pricingContactSales.companyName')}
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleChange}
                                                placeholder={t('website.pricingContactSales.companyNamePlaceholder')}
                                                className={`${styles.input} ${errors.companyName ? styles.errorInput : ''}`}
                                            />
                                            {errors.companyName ? <p className={styles.errorMessage}>{errors.companyName}</p> : null}
                                        </div>

                                        {/* Job Title */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>{t('website.pricingContactSales.jobTitle')}</label>
                                            <input 
                                                type="text" 
                                                name="jobTitle"
                                                value={formData.jobTitle}
                                                onChange={handleChange}
                                                placeholder={t('website.pricingContactSales.jobTitlePlaceholder')}
                                                className={styles.input}
                                            />
                                        </div>

                                        {/* Company Size */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                {t('website.pricingContactSales.companySize')}
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <select 
                                                name="companySize"
                                                value={formData.companySize}
                                                onChange={handleChange}
                                                className={`${styles.select} ${errors.companySize ? styles.errorInput : ''}`}
                                            >
                                                <option value="">{t('website.pricingContactSales.companySizePlaceholder')}</option>
                                                <option value="1-10">{t('website.pricingContactSales.companySizeOptions.oneToTen')}</option>
                                                <option value="11-50">{t('website.pricingContactSales.companySizeOptions.elevenToFifty')}</option>
                                                <option value="51-200">{t('website.pricingContactSales.companySizeOptions.fiftyOneToTwoHundred')}</option>
                                                <option value="201-500">{t('website.pricingContactSales.companySizeOptions.twoHundredOneToFiveHundred')}</option>
                                                <option value="501+">{t('website.pricingContactSales.companySizeOptions.fiveHundredPlus')}</option>
                                            </select>
                                            {errors.companySize ? <p className={styles.errorMessage}>{errors.companySize}</p> : null}
                                        </div>

                                        {/* Brand Count */}
                                        <div className={styles.field}>
                                            <label className={styles.label}>
                                                {t('website.pricingContactSales.brandCount')}
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <select 
                                                name="brandCount"
                                                value={formData.brandCount}
                                                onChange={handleChange}
                                                className={`${styles.select} ${errors.brandCount ? styles.errorInput : ''}`}
                                            >
                                                <option value="">{t('website.pricingContactSales.brandCountPlaceholder')}</option>
                                                <option value="1">1</option>
                                                <option value="2-5">2-5</option>
                                                <option value="6-10">6-10</option>
                                                <option value="11+">11+</option>
                                            </select>
                                            {errors.brandCount ? <p className={styles.errorMessage}>{errors.brandCount}</p> : null}
                                        </div>

                                        {/* Primary Use Case */}
                                        <div className={`${styles.field} ${styles.fullWidth}`}>
                                            <label className={styles.label}>
                                                {t('website.pricingContactSales.primaryUseCase')}
                                                <span className={styles.required}>*</span>
                                            </label>
                                            <select 
                                                name="primaryUseCase"
                                                value={formData.primaryUseCase}
                                                onChange={handleChange}
                                                className={`${styles.select} ${errors.primaryUseCase ? styles.errorInput : ''}`}
                                            >
                                                <option value="">{t('website.pricingContactSales.primaryUseCasePlaceholder')}</option>
                                                <option value="market-analysis">{t('website.pricingContactSales.primaryUseCaseOptions.marketAnalysis')}</option>
                                                <option value="content-creation">{t('website.pricingContactSales.primaryUseCaseOptions.contentCreation')}</option>
                                                <option value="product-research">{t('website.pricingContactSales.primaryUseCaseOptions.productResearch')}</option>
                                                <option value="competitor-tracking">{t('website.pricingContactSales.primaryUseCaseOptions.competitorTracking')}</option>
                                                <option value="other">{t('website.pricingContactSales.primaryUseCaseOptions.other')}</option>
                                            </select>
                                            {errors.primaryUseCase ? <p className={styles.errorMessage}>{errors.primaryUseCase}</p> : null}
                                        </div>

                                        {/* Needs */}
                                        <div className={`${styles.field} ${styles.fullWidth}`} style={{ gridColumn: '1 / -1' }}>
                                            <label className={styles.label}>{t('website.pricingContactSales.needs')}</label>
                                            <textarea 
                                                name="needs"
                                                value={formData.needs}
                                                onChange={handleChange}
                                                placeholder={t('website.pricingContactSales.needsPlaceholder')}
                                                rows={3}
                                                className={styles.textarea}
                                            />
                                        </div>
                                    </div>

                                    <button 
                                        type="submit" 
                                        className={styles.submitButton}
                                        style={{
                                            backgroundColor: '#1A162F',
                                            color: '#FFFFFF',
                                            borderColor: '#1A162F',
                                        }}
                                    >
                                        {t('website.pricingContactSales.submitRequest')}
                                    </button>
                  
                                    <p className={styles.privacyText}>
                                        {t('website.pricingContactSales.legalText')}
                                    </p>
                                </form>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default ContactSalesModal;
